import React, { useEffect, useMemo, useState } from "react";
import { apiConfig } from "../config/api.config";

export default function PresupuestoModal({
  isOpen,
  onClose,
  mode,
  requestType,
  existingRequest,
  onSubmit,
}) {
  const isRespond = mode === "respond";

  const requestId = existingRequest?.id || null;
  const contractId =
    existingRequest?.contract_id || existingRequest?.contractId || null;

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [fechaRespuesta, setFechaRespuesta] = useState("");
  const [numeroDocumento, setNumeroDocumento] = useState("");
  const [nuevoResumido, setNuevoResumido] = useState("");
  const [nombreNuevoResumido, setNombreNuevoResumido] = useState("");
  const [responseFile, setResponseFile] = useState(null);
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [motivoRechazo, setMotivoRechazo] = useState("");

  const title = useMemo(() => {
    if (requestType === "RP") return "Responder RP";
    if (requestType === "ANULAR_CDP") return "Responder Anulación CDP";
    if (requestType === "ARL") return "Responder ARL";
    return "Responder CDP";
  }, [requestType]);

  const pick = (obj, ...keys) => {
    for (const k of keys) {
      const v = obj?.[k];
      if (v !== undefined && v !== null) return v;
    }
    return null;
  };

  useEffect(() => {
    if (!isOpen) return;

    setError("");
    setSubmitting(false);

    const fechaFromApi =
      pick(existingRequest, "fecha_documento", "fechaDocumento", "responded_date");
    if (fechaFromApi) {
      const s = String(fechaFromApi);
      setFechaRespuesta(s.length >= 10 ? s.slice(0, 10) : s);
    } else {
      const today = new Date();
      const yyyy = String(today.getFullYear());
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      setFechaRespuesta(`${yyyy}-${mm}-${dd}`);
    }

    setNumeroDocumento(pick(existingRequest, "numero_documento", "numeroDocumento") || "");
    setNuevoResumido(pick(existingRequest, "nuevo_resumido", "nuevoResumido") || "");
    setNombreNuevoResumido(
      pick(existingRequest, "nombre_nuevo_resumido", "nombreNuevoResumido") || ""
    );

    const motivo = pick(existingRequest, "motivo_rechazo", "motivoRechazo") || "";
    setMotivoRechazo(motivo);

    const st =
      (pick(existingRequest, "response_status", "responseStatus", "status") || "")
        .toString()
        .toLowerCase();

    const isDenied = st === "negado" || st === "rejected" || st === "denied";
    setShowRejectBox(Boolean(isDenied || motivo));
  }, [isOpen, existingRequest]);

  if (!isOpen) return null;

  const safeClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const validatePDF = (file) => {
    if (!file) return "Debes adjuntar un documento PDF.";
    const isPdf =
      file.type === "application/pdf" ||
      (typeof file.name === "string" && file.name.toLowerCase().endsWith(".pdf"));
    if (!isPdf) return "El documento debe ser PDF.";
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) return "El PDF no puede superar 10MB.";
    return "";
  };

  const uploadResponseDocument = async () => {
    const fileErr = validatePDF(responseFile);
    if (fileErr) throw new Error(fileErr);

    const uploadUrl = apiConfig.getUrl(apiConfig.endpoints.uploadPresupuestoAttachment);
    const fd = new FormData();

    fd.append("file", responseFile);
    fd.append("folder", "presupuesto_respuestas");
    if (contractId) fd.append("contractId", contractId);
    if (requestId) fd.append("requestId", requestId);
    if (requestType) fd.append("type", requestType);

    const r = await fetch(uploadUrl, { method: "POST", body: fd });
    if (!r.ok) {
      const txt = await r.text().catch(() => "");
      throw new Error(txt || `Error subiendo documento (${r.status})`);
    }

    const j = await r.json().catch(() => null);
    const url = j?.url || j?.data?.url || j?.file?.url || null;
    const name = j?.originalName || j?.name || responseFile.name;

    if (!url) throw new Error("El upload no devolvió URL del archivo.");
    return { name, url };
  };

  const handleApprove = async () => {
    try {
      setSubmitting(true);
      setError("");

      if (!requestId) throw new Error("No se encontró id de la solicitud (request_id).");
      if (!contractId) throw new Error("No se encontró contract_id de la solicitud.");

      if (requestType !== "ARL") {
        if (!fechaRespuesta) throw new Error("Debes seleccionar la fecha de respuesta.");
        if (!numeroDocumento?.trim()) {
          throw new Error(
            requestType === "ANULAR_CDP"
              ? "Debes escribir el número del CDP anulado."
              : `Debes escribir el número del ${requestType}.`
          );
        }
      }

      const uploaded = await uploadResponseDocument();

      const payload = {
        requestId,
        request_id: requestId,
        contractId,
        contract_id: contractId,
        request_type: requestType,
        response_status: "aprobado",
        responded_at: new Date().toISOString(),
        fecha_documento: requestType !== "ARL" ? fechaRespuesta : null,
        numero_documento: requestType !== "ARL" ? numeroDocumento : null,
        nuevo_resumido: (requestType === "ANULAR_CDP" || requestType === "ARL") ? null : (nuevoResumido || null),
        nombre_nuevo_resumido: (requestType === "ANULAR_CDP" || requestType === "ARL") ? null : (nombreNuevoResumido || null),
        response_attachments: [uploaded],
        responseAttachments: [uploaded],
        motivo_rechazo: null,
        motivoRechazo: null,
        notes: requestType === "ARL" ? (nuevoResumido || null) : null,
      };

      await onSubmit(payload);
      safeClose();
    } catch (e) {
      console.error(e);
      setError(e?.message || "Error aprobando");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    try {
      setSubmitting(true);
      setError("");

      if (!requestId) throw new Error("No se encontró id de la solicitud (request_id).");
      if (!contractId) throw new Error("No se encontró contract_id de la solicitud.");
      if (!motivoRechazo.trim()) throw new Error("Debes escribir el motivo del rechazo.");

      const motivo = motivoRechazo.trim();

      const payload = {
        requestId,
        request_id: requestId,
        contractId,
        contract_id: contractId,
        request_type: requestType,
        response_status: "negado",
        responded_at: new Date().toISOString(),
        fecha_documento: requestType !== "ARL" ? (fechaRespuesta || null) : null,
        numero_documento: requestType !== "ARL" ? (numeroDocumento || null) : null,
        motivo_rechazo: motivo,
        motivoRechazo: motivo,
        response_attachments: null,
        responseAttachments: null,
        nuevo_resumido: (requestType === "ANULAR_CDP" || requestType === "ARL") ? null : (nuevoResumido || null),
        nombre_nuevo_resumido: (requestType === "ANULAR_CDP" || requestType === "ARL") ? null : (nombreNuevoResumido || null),
      };

      await onSubmit(payload);
      safeClose();
    } catch (e) {
      console.error(e);
      setError(e?.message || "Error negando");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "16px",
      }}
      onClick={safeClose}
    >
      <div
        style={{
          width: "min(920px, 100%)",
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 12px 35px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 18px",
            borderBottom: "1px solid #e9eef5",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <div style={{ fontWeight: 700, fontSize: "18px" }}>{title}</div>
          <button
            type="button"
            onClick={safeClose}
            style={{ border: "none", background: "transparent", fontSize: "20px", cursor: "pointer" }}
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "18px" }}>
          {!isRespond && (
            <div style={{ padding: "10px", background: "#f7f9fc", borderRadius: "8px" }}>
              Modo: {String(mode)}
            </div>
          )}

          {error && (
            <div style={{
              marginBottom: "12px", padding: "10px 12px",
              background: "#fdecec", border: "1px solid #f5c2c2",
              color: "#a52020", borderRadius: "8px",
            }}>
              {error}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", marginBottom: "14px" }}>

            {/* Fecha — oculto para ARL */}
            {requestType !== "ARL" && (
              <div>
                <label style={{ display: "block", fontSize: "12px", color: "#384656", marginBottom: "6px" }}>
                  Fecha de respuesta
                </label>
                <input
                  type="date"
                  value={fechaRespuesta}
                  onChange={(e) => setFechaRespuesta(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #d7e1ee", outline: "none" }}
                />
              </div>
            )}

            {/* Número documento — oculto para ARL */}
            {requestType !== "ARL" && (
              <div>
                <label style={{ display: "block", fontSize: "12px", color: "#384656", marginBottom: "6px" }}>
                  {requestType === "ANULAR_CDP" ? "Número del CDP anulado" : `Número del ${requestType}`}
                </label>
                <input
                  value={numeroDocumento}
                  onChange={(e) => setNumeroDocumento(e.target.value)}
                  placeholder={
                    requestType === "ANULAR_CDP"
                      ? "Ej: CDP-1020"
                      : requestType === "CDP"
                      ? "Ej: cdp-1020"
                      : "Ej: rp-1020"
                  }
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #d7e1ee", outline: "none" }}
                />
              </div>
            )}

            {/* Nuevo resumido — oculto para ANULAR_CDP y ARL */}
            {requestType !== "ANULAR_CDP" && requestType !== "ARL" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: "12px", color: "#384656", marginBottom: "6px" }}>
                  Nuevo resumido
                </label>
                <textarea
                  value={nuevoResumido}
                  onChange={(e) => setNuevoResumido(e.target.value)}
                  rows={4}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #d7e1ee", outline: "none", resize: "vertical" }}
                />
              </div>
            )}

            {/* Nombre nuevo resumido — oculto para ANULAR_CDP y ARL */}
            {requestType !== "ANULAR_CDP" && requestType !== "ARL" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: "12px", color: "#384656", marginBottom: "6px" }}>
                  Nombre del nuevo resumido
                </label>
                <input
                  value={nombreNuevoResumido}
                  onChange={(e) => setNombreNuevoResumido(e.target.value)}
                  placeholder="Ej: Resumido CDP ajustado"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #d7e1ee", outline: "none" }}
                />
              </div>
            )}

            {/* Documento PDF */}
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ display: "block", fontSize: "12px", color: "#384656", marginBottom: "6px" }}>
                Documento de respuesta (PDF)
              </label>
              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setResponseFile(e.target.files?.[0] || null)}
              />
              <div style={{ fontSize: "12px", color: "#6b7785", marginTop: "6px" }}>
                Se adjunta en la respuesta al aprobar. Máximo 10MB.
              </div>
            </div>

            {/* Notas — solo para ARL */}
            {requestType === "ARL" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ display: "block", fontSize: "12px", color: "#384656", marginBottom: "6px" }}>
                  Notas (opcional)
                </label>
                <textarea
                  value={nuevoResumido}
                  onChange={(e) => setNuevoResumido(e.target.value)}
                  rows={3}
                  placeholder="Observaciones sobre la vinculación ARL..."
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #d7e1ee", outline: "none", resize: "vertical" }}
                />
              </div>
            )}
          </div>

          {/* Caja Negar */}
          {showRejectBox && (
            <div style={{
              padding: "12px", borderRadius: "10px",
              background: "#fff7f7", border: "1px solid #f1c7c7", marginTop: "8px",
            }}>
              <div style={{ fontWeight: 700, marginBottom: "8px", color: "#7a1a1a" }}>
                Motivo del rechazo
              </div>
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                rows={4}
                placeholder="Escribe el motivo..."
                style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #e1b2b2", outline: "none", resize: "vertical" }}
              />
              <div style={{ marginTop: "10px", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => { setShowRejectBox(false); setMotivoRechazo(""); }}
                  disabled={submitting}
                  style={{ padding: "9px 14px", borderRadius: "8px", border: "1px solid #d0d7e2", background: "#fff", cursor: "pointer" }}
                >
                  Cancelar rechazo
                </button>
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={submitting}
                  style={{ padding: "9px 14px", borderRadius: "8px", border: "none", background: "#c62828", color: "#fff", cursor: "pointer", fontWeight: 700 }}
                >
                  Confirmar Negar
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 18px", borderTop: "1px solid #e9eef5",
          display: "flex", gap: "10px", justifyContent: "flex-end",
        }}>
          <button
            type="button"
            onClick={safeClose}
            disabled={submitting}
            style={{ padding: "9px 14px", borderRadius: "8px", border: "1px solid #d0d7e2", background: "#fff", cursor: "pointer" }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => setShowRejectBox(true)}
            disabled={submitting}
            style={{ padding: "9px 14px", borderRadius: "8px", border: "none", background: "#c62828", color: "#fff", cursor: "pointer", fontWeight: 700 }}
          >
            Negar
          </button>
          <button
            type="button"
            onClick={handleApprove}
            disabled={submitting}
            style={{ padding: "9px 14px", borderRadius: "8px", border: "none", background: "#2e7d32", color: "#fff", cursor: "pointer", fontWeight: 700 }}
          >
            {submitting ? "Guardando..." : "Aprobar"}
          </button>
        </div>
      </div>
    </div>
  );
}